import { useEffect, useRef } from 'react';
import Popup from 'reactjs-popup';
import { FaEllipsis } from 'react-icons/fa6';
import 'styles/global.css';
import { Button } from 'react-bootstrap';

/**
 * Defines a circular ellipsis button and the popup controls for it.
 * @param {{options: {component: ()}[], open: boolean, onOpen: (), onClose: (), closeOption?: boolean, containerClassname?: string, triggerClassname?: string}}
 * @example <EllipsisMenu
               options={[
                 {
                   component: (
                     <div onClick={option1Clicked}>Option 1</div>
                   ),
                 },
                 {
                   component: (
                     <div onClick={option2Clicked}>Option 2</div>
                   ),
                 },
               ]}
               open={ellipsisOpen}
               onOpen={() => setEllipsisOpen(true)}
               onClose={() => setEllipsisOpen(false)}
             />
 * @returns React Component
 */
export default function EllipsisMenu({
  options,
  open,
  closeOption,
  onOpen,
  onClose,
  triggerOverride,
  containerClassname = '',
  closeClassName = '',
  children,
  triggerClassname = '',
  triggerStyle = {},
}) {
  const popupRef = useRef(null);

  //open handler
  useEffect(() => {
    if (open) popupRef.current?.open();
    else popupRef.current?.close();
  }, [open]);

  useEffect(() => {
    const handleResize = () => {
      popupRef.current?.close();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  //onclick events
  const close = () => {
    popupRef.current?.close();
  };

  //trigger component
  const popupTrigger = () => {
    return (
      <div
        className={`rounded-circle d-flex justify-content-center align-items-center cursor-pointer text-white bg-primary ${triggerClassname}`}
        style={{
          width: 30,
          height: 30,
          minWidth: 30,
          minHeight: 30,
          flexShrink: 0,
          flexGrow: 0,
          alignSelf: 'center',
          ...triggerStyle,
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <FaEllipsis />
      </div>
    );
  };

  if ((options && Array.isArray(options) && options.length > 0) || children)
    return (
      <Popup
        ref={popupRef}
        on={'click'}
        trigger={triggerOverride || popupTrigger()}
        onOpen={onOpen}
        onClose={onClose}
        position={'bottom center'}
        closeOnDocumentClick
      >
        <div
          className={['d-flex flex-column bg-white rounded-4 user-select-none', containerClassname]
            .filter(Boolean)
            .join(' ')}
        >
          {options?.map((option, index) => (
            <div key={index} className="border-bottom border-1 border-black">
              {option?.component}
            </div>
          ))}

          {Array.isArray(children) &&
            children?.map((child, index) => (
              <div
                key={index}
                className={
                  index < children.length - 1 || closeOption
                    ? 'border-bottom border-1 border-black'
                    : ''
                }
              >
                {child}
              </div>
            ))}

          {children && !Array.isArray(children) && <>{children}</>}

          {closeOption && (
            <div
              className={[
                'border-bottom-0 cursor-pointer text-center fw-bold btn text-dark',
                closeClassName,
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={close}
            >
              Close
            </div>
          )}
        </div>
      </Popup>
    );
}
